/**
 * 游戏时间快照工具。
 * 提供时间快照 Schema、元数据键与格式化/排序函数，供 event、history 等模块共用，
 * 避免非 history 模块为使用时间工具而反向依赖 history。
 */

import { z } from 'zod';
import { GameTimeSchema, type GameTime } from './types/world';

// ============================================
// Schema 与类型
// ============================================

/**
 * 游戏时间快照 Schema（完整 GameTime 的可排序子集）。
 */
export const GameTimeSnapshotSchema = GameTimeSchema.pick({
  year: true,
  season: true,
  month: true,
  day: true,
  hour: true,
  minute: true,
});

export type GameTimeSnapshot = z.infer<typeof GameTimeSnapshotSchema>;

// ============================================
// 元数据键
// ============================================

// 注意：键的字面量参与存档与历史条目元数据，属持久化契约，不得变更。
export const GAME_TIME_SNAPSHOT_METADATA_KEY = 'gameTimeSnapshot';
export const GAME_TIME_ORDER_METADATA_KEY = 'gameTimeOrder';

// ============================================
// 格式化与排序
// ============================================

/**
 * 格式化为紧凑游戏时间文本，如 `3月2日 08:30`。
 */
export function formatCompactGameTime(time?: GameTime | GameTimeSnapshot | null): string | undefined {
  if (!time) return undefined;

  const parsed = GameTimeSnapshotSchema.safeParse(time);
  if (!parsed.success) return undefined;

  return `${parsed.data.month}月${parsed.data.day}日 ${String(parsed.data.hour).padStart(2, '0')}:${String(parsed.data.minute).padStart(2, '0')}`;
}

/**
 * 把时间快照折算为可比较的排序序号。
 */
export function toGameTimeOrder(time: GameTimeSnapshot): number {
  return (((time.year * 12 + time.month) * 100 + time.day) * 1440) + time.hour * 60 + time.minute;
}

/**
 * 在元数据上附加时间快照与排序序号；时间缺失或非法时原样返回元数据副本。
 */
export function buildGameTimeMetadata(
  time?: GameTime | GameTimeSnapshot | null,
  metadata?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!time) {
    return metadata ? { ...metadata } : undefined;
  }

  const parsed = GameTimeSnapshotSchema.safeParse(time);
  if (!parsed.success) {
    return metadata ? { ...metadata } : undefined;
  }

  return {
    ...(metadata || {}),
    [GAME_TIME_SNAPSHOT_METADATA_KEY]: parsed.data,
    [GAME_TIME_ORDER_METADATA_KEY]: toGameTimeOrder(parsed.data),
  };
}
